import { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';

import { alertService } from '../../../../shared/services/alertService';
import { usePlanningWizard } from '../PlanningWizardContext';
import { OrderTemplate } from '../types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Order-template fields that are required (Angular Validators.required). */
const REQUIRED_FIELDS: (keyof OrderTemplate)[] = [
  'Header',
  'Reference',
  'Reference2',
  'ContentBefore',
  'ContentAfter',
  'SignAuth',
  'CopyTo',
];

/**
 * Static candidate-detail rows shown in the preview for non-grid counselling
 * types (Angular CounsellingType != 4). Values are placeholders ("-").
 */
const CANDIDATE_DETAIL_ROWS: string[] = [
  'குறிப்பு எண்',
  'எமிஸ் எண்',
  'ஆசிரியர் எண்',
  'பெயர்',
  'மூப்பு எண்',
  'தற்போதைய பதவி & பாடம்',
  'தற்போது பணிபுரியும் பள்ளியின் பெயர், UDISE, தொகுதி, மாவட்டம் ',
  'மாறுதல் செய்யப்படும் பதவி & பாடம்',
  'மாறுதல் செய்யப்படும் பள்ளியின் பெயர், UDISE, தொகுதி, மற்றும் மாவட்டம்',
  'முன்னுரிமை',
  'இடுகை வகை',
];

/**
 * Step 8 — Order Template (Angular handleTabView / handleTabSave /
 * handleHTMLPreview). Rich text via CKEditor. For Promotion (CounsellingType
 * 3) there are two tabs: Order Copy (Type 1) and Relinguish (Type 2), each
 * pushed to orderArray. HTML preview renders the composed order copy.
 */
const StepOrderTemplate = () => {
  const {
    orderTemplate,
    orderArray,
    setOrderArray,
    loadOrderTemplate,
    detailsForm,
    addedTableGridList,
    nextPage,
    previousPage,
  } = usePlanningWizard();

  const counsellingType = String(detailsForm.values.CounsellingType);
  const isPromotion = counsellingType === '3';

  const [activeTab, setActiveTab] = useState(0);
  const [previewModal, setPreviewModal] = useState(false);

  const v = orderTemplate.values;
  const setField = (name: keyof OrderTemplate, value: string) =>
    orderTemplate.setFieldValue(name as string, value);

  const isValid = () => REQUIRED_FIELDS.every((f) => !!v[f]);

  // Type string for a tab index: 0 = Order Copy (Type 1), 1 = Relinguish (2).
  const typeForTab = (index: number) => (index === 0 ? '1' : '2');

  // Upsert the given tab's values into orderArray (the durable store that
  // survives leaving/returning to this step). Replaces an existing entry of
  // the same Type instead of appending a duplicate.
  const commitToOrderArray = (values: OrderTemplate, type: string) => {
    setOrderArray((prev) => {
      const rest = prev.filter((o) => String(o.Type) !== type);
      return [...rest, { ...values, Type: type }];
    });
  };

  // Load a tab's values from orderArray if present; otherwise fetch from the
  // server (first visit / edit mode). orderArray persists across remounts, so
  // returning to this step restores whatever was committed.
  const loadTab = (index: number) => {
    const type = typeForTab(index);
    const saved = orderArray.find((o) => String(o.Type) === type);
    if (saved) {
      orderTemplate.setValues({ ...saved });
    } else {
      loadOrderTemplate(index === 0 ? 1 : 2);
    }
  };

  const handleTabView = (index: number) => {
    // Stash the current tab's values into the durable store before leaving.
    commitToOrderArray(orderTemplate.values, typeForTab(activeTab));
    setActiveTab(index);
    loadTab(index);
  };

  // On (re)mount — e.g. returning to this step from Seniority — restore the
  // active tab's committed values from orderArray so nothing looks empty.
  useEffect(() => {
    loadTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Previous: persist the current tab's edits into the durable store before
  // leaving, so returning to this step restores them (not a stale snapshot).
  const handlePrevious = () => {
    commitToOrderArray(orderTemplate.values, typeForTab(activeTab));
    previousPage();
  };

  const handleTabSave = () => {
    // Bump Formik's submitCount so the inline "<field> is required" messages
    // become visible (they are gated on submitCount > 0).
    orderTemplate.submitForm();
    if (!isValid()) {
      alertService.error('Please fill all fields');
      return;
    }
    if (isPromotion) {
      if (activeTab === 0) {
        // Commit Order Copy (Type 1) and move to the Relinguish tab.
        commitToOrderArray(orderTemplate.values, '1');
        setActiveTab(1);
        loadTab(1);
      } else {
        // Commit Relinguish (Type 2) and advance to the next step.
        commitToOrderArray(orderTemplate.values, '2');
        nextPage();
      }
    } else {
      // Non-promotion: single tab. Commit its edits (Type 1) before advancing
      // so returning to this step restores them instead of a stale snapshot.
      commitToOrderArray(orderTemplate.values, '1');
      nextPage();
    }
  };

  const editorField = (label: string, name: keyof OrderTemplate) => (
    <div className="row mb-3">
      <div className="col-3">
        <label className="inMandatory">{label}</label>
      </div>
      <div className="col-9">
        <CKEditor
          editor={ClassicEditor as any}
          data={(v[name] as string) || ''}
          onChange={(_e: any, editor: any) => setField(name, editor.getData())}
        />
        {REQUIRED_FIELDS.includes(name) &&
          orderTemplate.submitCount > 0 &&
          !v[name] && <small className="p-error">{label} is required</small>}
      </div>
    </div>
  );

  const orderForm = (
    <>
      <br />
      {editorField('Header', 'Header')}
      {editorField('Sub 1 (பொருள்)', 'Reference')}
      {editorField('Sub 2 (பார்வை)', 'Reference2')}
      {editorField('Content Before Candidate Details', 'ContentBefore')}
      {editorField('Content After Candidate Details', 'ContentAfter')}
      <div className="row mb-3">
        <div className="col-3">
          <label className="inMandatory">Signing Authority</label>
        </div>
        <div className="col-9">
          <InputTextarea
            value={v.SignAuth}
            rows={6}
            autoResize
            className="form-control"
            onChange={(e) => setField('SignAuth', e.target.value)}
          />
          {orderTemplate.submitCount > 0 && !v.SignAuth && (
            <small className="p-error">Signing Authority is required</small>
          )}
        </div>
      </div>
      {editorField('Copy To (நகல்)', 'CopyTo')}
    </>
  );

  const footer = (
    <div className="row mt-2">
      <div className="col-6">
        <Button
          type="button"
          icon="pi pi-angle-left"
          label="Previous"
          className="p-button-danger"
          onClick={handlePrevious}
        />
      </div>
      <div className="col-6 text-end">
        <Button
          type="button"
          label="Preview"
          className="p-button-primary"
          onClick={() => setPreviewModal(true)}
        />
        &nbsp;
        <Button
          type="button"
          icon="pi pi-angle-right"
          iconPos="right"
          label="Next"
          className="p-button-success"
          onClick={handleTabSave}
        />
      </div>
    </div>
  );

  return (
    <div className="card p-4">
      <div className="row mb-4">
        <div className="col text-center">
          <h5>Order Template</h5>
        </div>
      </div>

      <TabView
        activeIndex={activeTab}
        onTabChange={(e) => handleTabView(e.index)}
      >
        <TabPanel header="Order Copy">
          {orderForm}
          {footer}
        </TabPanel>
        {isPromotion && (
          <TabPanel header="Relinguish">
            {orderForm}
            {footer}
          </TabPanel>
        )}
      </TabView>

      <Dialog
        headerClassName="dialog-header-secondary"
        header="Order Copy Preview"
        modal
        visible={previewModal}
        draggable={false}
        onHide={() => setPreviewModal(false)}
        style={{ width: '70vw' }}
      >
        <div className="card mt-2 p-4 mt-4 plan-order-preview">
          <div
            className="col-12 text-center"
            dangerouslySetInnerHTML={{ __html: v.Header }}
          />
          <br />
          <table>
            <tbody>
              <tr>
                <td>
                  <b>பொருள்</b>
                </td>
                <td dangerouslySetInnerHTML={{ __html: v.Reference }} />
              </tr>
              <tr>
                <td>
                  <b>பார்வை</b>
                </td>
                <td dangerouslySetInnerHTML={{ __html: v.Reference2 }} />
              </tr>
            </tbody>
          </table>
          <div className="text-center">*****</div>
          <div dangerouslySetInnerHTML={{ __html: v.ContentBefore }} />
          {counsellingType === '4' && (
            <table className="width-table">
              <tbody>
                {addedTableGridList
                  .filter((item) => item.key)
                  .map((item, ind) => (
                    <tr key={ind}>
                      <td style={{ width: '50%' }}>{item.key}</td>
                      <td style={{ width: '50%' }}>-</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
          {counsellingType !== '4' && (
            <table className="width-table">
              <tbody>
                {CANDIDATE_DETAIL_ROWS.map((row, ind) => (
                  <tr key={ind}>
                    <td>{row}</td>
                    <td>-</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div dangerouslySetInnerHTML={{ __html: v.ContentAfter }} />
          <div
            className="text-end"
            dangerouslySetInnerHTML={{ __html: v.SignAuth }}
          />
          <div dangerouslySetInnerHTML={{ __html: v.CopyTo }} />
        </div>
        <div className="row mt-2">
          <div className="col-12 text-end">
            <Button
              type="button"
              label="Close"
              className="p-button-danger"
              onClick={() => setPreviewModal(false)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default StepOrderTemplate;
